import React, { useState, useEffect, useRef } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingSidebar.scss';
import SidebarCard from '../onboardingComponents/SidebarCard';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import window1Card1Back from '../../assets/onboarding/sidebar/card1/window1.svg';
import window1Card1Front from '../../assets/onboarding/sidebar/card1/window2.svg';
import window2Card2Back from '../../assets/onboarding/sidebar/card2/window1.svg';
import window2Card2Front from '../../assets/onboarding/sidebar/card2/window2.svg';
import window3Card3Back from '../../assets/onboarding/sidebar/card3/window1.svg';
import window3Card3Front from '../../assets/onboarding/sidebar/card3/window2.png';
import askTaraTag from '../../assets/onboarding/sidebar/card4/ask-tara-tag.svg';

interface OnboardingSidebarProps {
  currentStep: number;
  onStepChange: (stepNumber: number) => void;
  isStepCompleted: (stepNumber: number) => boolean;
  isStepAccessible: (stepNumber: number) => boolean;
}

type PromotionalCard =
  | {
      type: 'layered';
      frontPosition?: 'bottom-left' | 'center';
      title: string;
      subtitle: string;
      description: string;
    }
  | {
      type: 'single';
      title: string;
      subtitle: string;
      description: string;
    };

const promotionalCards: PromotionalCard[] = [
  {
    type: 'layered',
    title: 'Automated reporting',
    subtitle: 'Smart, Unified, Instant',
    description:
      'Connect once and let TARA handle the rest. Get clear, automated reports across every channel - always up-to-date, always ready to share.',
  },
  {
    type: 'layered',
    title: 'Multi-brand management',
    subtitle: 'All Brands, One Place',
    description:
      'Manage all your clients from a single dashboard. Add brands, connect their ad accounts, and get instant cross-client insights.',
  },
  {
    type: 'layered',
    frontPosition: 'center',
    title: 'Monitoring',
    subtitle: 'Always in Control',
    description:
      'TARA keeps an eye on your platforms 24/7. It flags unusual trends, drops, or engagement spikes, helping you fix issues before they impact your business.',
  },
  {
    type: 'layered',
    title: 'Powered by Claude',
    subtitle: 'AI That Understands Your Business',
    description:
      'TARA connects your marketing data directly to Claude, giving you an AI assistant that truly understands your campaigns, metrics, and growth opportunities.',
  },
  {
    type: 'single',
    title: 'Conversational AI',
    subtitle: 'Ask anything, Anytime',
    description:
      "No more digging through dashboards. Just ask, and get instant answers powered by TARA's AI - insights made simple, anytime you need them.",
  },
];

const OnboardingSidebar: React.FC<OnboardingSidebarProps> = ({
  currentStep,
  onStepChange,
  isStepCompleted,
  isStepAccessible,
}) => {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const imageLoadCountRef = useRef(0);

  const handleImageLoad = (imageKey: string) => {
    setLoadedImages((prev) => {
      const newSet = new Set(prev);
      newSet.add(imageKey);
      return newSet;
    });
    imageLoadCountRef.current += 1;
  };

  useEffect(() => {
    const checkImageLoaded = (src: string, key: string) => {
      const img = new Image();
      img.onload = () => handleImageLoad(key);
      img.onerror = () => handleImageLoad(key);
      img.src = src;
    };

    checkImageLoaded(window1Card1Back, 'step1-back');
    checkImageLoaded(window1Card1Front, 'step1-front');
    checkImageLoaded(window2Card2Back, 'step2-back');
    checkImageLoaded(window2Card2Front, 'step2-front');
    checkImageLoaded(window3Card3Back, 'step3-back');
    checkImageLoaded(window3Card3Front, 'step3-front');
    checkImageLoaded(askTaraTag, 'step5-single');
  }, []);

  const steps = [
    {
      stepNumber: 1,
      title: 'Tell us about your business',
      subtitle: 'Select your business type',
    },
    {
      stepNumber: 2,
      title: 'Add your client',
      subtitle: 'Add your website or brand URLs',
    },
    {
      stepNumber: 3,
      title: 'Connect with Claude',
      subtitle: 'Set up AI-powered analysis',
    },
    {
      stepNumber: 4,
      title: 'Connect your integrations',
      subtitle: 'Connect your apps and data sources',
    },
    {
      stepNumber: 5,
      title: 'Get started',
      subtitle: 'Start using TARA with your data',
    },
  ];

  const currentPromoCard =
    promotionalCards[currentStep - 1] || promotionalCards[0];

  return (
    <div className='onboardingSidebar'>
      <div className='onboardingSidebar__header'>
        <img
          src={adupLogoSet}
          alt='TARA AdUp'
          className='onboardingSidebar__logo'
        />
      </div>

      <div className='onboardingSidebar__steps'>
        {steps.map((step) => {
          const stepCompleted = isStepCompleted(step.stepNumber);
          const isClickable = isStepAccessible(step.stepNumber);
          return (
            <SidebarCard
              key={step.stepNumber}
              stepNumber={step.stepNumber}
              title={step.title}
              subtitle={step.subtitle}
              isActive={currentStep === step.stepNumber}
              isCompleted={stepCompleted}
              isDisabled={!isClickable}
              onClick={() => {
                if (isClickable) {
                  onStepChange(step.stepNumber);
                }
              }}
            />
          );
        })}
      </div>

      <div
        className={`onboardingSidebar__promo ${
          currentStep === 5 ? 'onboardingSidebar__promo--last' : ''
        }`}
      >
        <div
          className={`onboardingSidebar__promo-image ${
            currentPromoCard.type === 'single'
              ? 'onboardingSidebar__promo-image--single'
              : ''
          }`}
          key={`promo-${currentStep}-${loadedImages.size}`}
        >
          {/* Step 1 promo images */}
          <div
            className={`onboardingSidebar__promo-layered ${
              currentStep === 1
                ? ''
                : 'onboardingSidebar__promo-layered--hidden'
            }`}
          >
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--back'>
              <img
                src={window1Card1Back}
                alt='Automated reporting - Back'
                onLoad={() => handleImageLoad('step1-back')}
                onError={() => handleImageLoad('step1-back')}
              />
            </div>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--front'>
              <img
                src={window1Card1Front}
                alt='Automated reporting - Front'
                onLoad={() => handleImageLoad('step1-front')}
                onError={() => handleImageLoad('step1-front')}
              />
            </div>
          </div>
          {/* Step 2 promo images - reuse card2 assets */}
          <div
            className={`onboardingSidebar__promo-layered ${
              currentStep === 2
                ? ''
                : 'onboardingSidebar__promo-layered--hidden'
            }`}
          >
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--back'>
              <img
                src={window2Card2Back}
                alt='Multi-brand management - Back'
                onLoad={() => handleImageLoad('step2-back')}
                onError={() => handleImageLoad('step2-back')}
              />
            </div>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--front'>
              <img
                src={window2Card2Front}
                alt='Multi-brand management - Front'
                onLoad={() => handleImageLoad('step2-front')}
                onError={() => handleImageLoad('step2-front')}
              />
            </div>
          </div>
          {/* Step 3 promo images - reuse card3 assets */}
          <div
            className={`onboardingSidebar__promo-layered ${
              currentStep === 3
                ? ''
                : 'onboardingSidebar__promo-layered--hidden'
            }`}
          >
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--back'>
              <img
                src={window3Card3Back}
                alt='Monitoring - Back'
                onLoad={() => handleImageLoad('step3-back')}
                onError={() => handleImageLoad('step3-back')}
              />
            </div>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--front onboardingSidebar__promo-layer--front-center'>
              <img
                src={window3Card3Front}
                alt='Monitoring - Front'
                onLoad={() => handleImageLoad('step3-front')}
                onError={() => handleImageLoad('step3-front')}
              />
            </div>
          </div>
          {/* Step 4 promo - reuse card1 assets for plugin step */}
          <div
            className={`onboardingSidebar__promo-layered ${
              currentStep === 4
                ? ''
                : 'onboardingSidebar__promo-layered--hidden'
            }`}
          >
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--back'>
              <img
                src={window1Card1Back}
                alt='AI Optimize - Back'
              />
            </div>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--front'>
              <img
                src={window1Card1Front}
                alt='AI Optimize - Front'
              />
            </div>
          </div>
          {/* Step 5 promo - single image */}
          <img
            src={askTaraTag}
            alt='Conversational AI'
            className={
              currentStep === 5 ? '' : 'onboardingSidebar__promo-image--hidden'
            }
            onLoad={() => handleImageLoad('step5-single')}
            onError={() => handleImageLoad('step5-single')}
          />
        </div>
        <div className='onboardingSidebar__promo-content'>
          <h3 className='onboardingSidebar__promo-title'>
            {currentPromoCard.title}
          </h3>
          <h4 className='onboardingSidebar__promo-subtitle'>
            {currentPromoCard.subtitle}
          </h4>
          <p className='onboardingSidebar__promo-description'>
            {currentPromoCard.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSidebar;
